import { useRef, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Header from '../Header';
import GettingStartedActions from '../GettingStartedActions';
import SupportedToolIcons from '../SupportedToolIcons';
import {appEntryDestination} from '../../client/authContinuation';
import Footer from '../Footer';
import TestimonialsSection from '../sections/TestimonialsSection';
import ClientInstallInstructions,{useClientInstallConfig,clientInstallAvailable} from '../../client/ClientInstallInstructions';

/* ── Scroll reveal hook ── */
function useInView(threshold = 0.2) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ── Word-by-word headline reveal ── */
const WordReveal = ({ text, className, style }) => {
  const [ref, inView] = useInView(0.2);
  const words = text.split(' ');
  return (
    <h2 ref={ref} className={className} style={style}>
      {words.map((word, i) => (
        <span
          key={i}
          style={{
            display: 'inline-block',
            marginRight: '0.28em',
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateY(0)' : 'translateY(16px)',
            transition: `opacity 0.55s ease ${i * 0.075}s, transform 0.55s ease ${i * 0.075}s`,
          }}
        >
          {word}
        </span>
      ))}
    </h2>
  );
};

/* ── Staggered list ── */
const StaggeredList = ({ children }) => {
  const [ref, inView] = useInView(0.15);
  const items = Array.isArray(children) ? children : [children];
  return (
    <div ref={ref} className="mt-10 flex flex-col gap-3">
      {items.map((child, i) => (
        <div
          key={i}
          style={{
            opacity: inView ? 1 : 0,
            transform: inView ? 'translateX(0)' : 'translateX(-14px)',
            transition: `opacity 0.45s ease ${i * 0.08}s, transform 0.45s ease ${i * 0.08}s`,
          }}
        >
          {child}
        </div>
      ))}
    </div>
  );
};

/* ── Particle field — dots that repel from mouse (section bg effect) ── */
const ParticleField = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let mouse = { x: -9999, y: -9999 };
    let animId;
    const spacing = 54;
    let particles = [];

    const buildParticles = () => {
      particles = [];
      const cols = Math.ceil(canvas.width / spacing) + 1;
      const rows = Math.ceil(canvas.height / spacing) + 1;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          particles.push({ ox: c * spacing, oy: r * spacing, x: c * spacing, y: r * spacing, vx: 0, vy: 0 });
        }
      }
    };

    const resize = () => {
      const parent = canvas.parentElement;
      canvas.width = parent ? parent.offsetWidth : window.innerWidth;
      canvas.height = parent ? parent.offsetHeight : window.innerHeight;
      buildParticles();
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const repelRadius = 160;

      for (const p of particles) {
        const dx = mouse.x - p.x;
        const dy = mouse.y - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < repelRadius && dist > 0) {
          const force = (1 - dist / repelRadius) * 55;
          p.vx -= (dx / dist) * force * 0.06;
          p.vy -= (dy / dist) * force * 0.06;
        }

        // Spring back to origin
        p.vx += (p.ox - p.x) * 0.09;
        p.vy += (p.oy - p.y) * 0.09;
        p.vx *= 0.78;
        p.vy *= 0.78;
        p.x += p.vx;
        p.y += p.vy;

        const disp = Math.sqrt((p.x - p.ox) ** 2 + (p.y - p.oy) ** 2);
        const alpha = 0.07 + Math.min(disp / 25, 1) * 0.13;

        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${alpha})`;
        ctx.fill();
      }

      animId = requestAnimationFrame(draw);
    };

    const onMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    resize();
    draw();
    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', onMouseMove);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
    />
  );
};

/* ── Force field canvas — directional lines (hero only) ── */
const ForceField = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let mouse = { x: -9999, y: -9999 };
    let animId;
    const spacing = 48;
    const lineLen = 10;
    let angles = [];

    const resize = () => {
      const parent = canvas.parentElement;
      canvas.width = parent ? parent.offsetWidth : window.innerWidth;
      canvas.height = parent ? parent.offsetHeight : window.innerHeight;
      const cols = Math.ceil(canvas.width / spacing) + 1;
      const rows = Math.ceil(canvas.height / spacing) + 1;
      angles = Array.from({ length: rows }, () => new Array(cols).fill(0));
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.lineWidth = 1;
      const cols = Math.ceil(canvas.width / spacing) + 1;
      const rows = Math.ceil(canvas.height / spacing) + 1;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const x = c * spacing;
          const y = r * spacing;
          const dx = mouse.x - x;
          const dy = mouse.y - y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const influence = Math.max(0, 1 - dist / 300);
          const targetAngle = influence > 0 ? Math.atan2(dy, dx) : 0;
          if (!angles[r]) angles[r] = [];
          angles[r][c] = (angles[r][c] || 0) + (targetAngle - (angles[r][c] || 0)) * 0.07;
          const a = angles[r][c];
          const len = lineLen + influence * 18;
          ctx.strokeStyle = `rgba(255,255,255,${0.08 + influence * 0.18})`;
          ctx.lineWidth = 1 + influence * 0.6;
          ctx.beginPath();
          ctx.moveTo(x - Math.cos(a) * len / 2, y - Math.sin(a) * len / 2);
          ctx.lineTo(x + Math.cos(a) * len / 2, y + Math.sin(a) * len / 2);
          ctx.stroke();
        }
      }
      animId = requestAnimationFrame(draw);
    };

    const onMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    resize();
    draw();
    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', onMouseMove);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
    />
  );
};

/* ── CTAs ── */
const CTAButton = ({installHref}) => (
  <Link
    to={installHref}
    className="inline-flex items-center gap-2 rounded-full bg-brand-50 px-8 py-4 text-base font-semibold text-brand-900 no-underline hover:bg-brand-200 transition-colors"
  >
    Install Pabs
    <ArrowRight className="h-4 w-4" />
  </Link>
);

const SecondaryCTA = ({ to, label }) => (
  <Link
    to={to}
    className="inline-flex items-center gap-1 text-sm font-medium text-brand-400 no-underline hover:text-brand-50 transition-colors"
  >
    {label}
    <ArrowRight className="h-3.5 w-3.5" />
  </Link>
);

const CTA = ({ secondary, installHref }) => (
  <div className="mt-10 flex items-center gap-6">
    <CTAButton installHref={installHref}/>
    {secondary && <SecondaryCTA to={secondary.to} label={secondary.label} />}
  </div>
);

const DotDivider = () => (
  <div className="h-px w-full bg-brand-800" aria-hidden="true" />
);

/* ── Page ── */
export default function SimpleHomepageDemo() {
  const installConfig=useClientInstallConfig();
  const installAvailable=clientInstallAvailable(installConfig);
  const installHref=installAvailable?'#install':'/docs/install-pabs';
  const location=useLocation();
  useEffect(()=>{
    if(location.hash==='#install' && clientInstallAvailable(installConfig)) document.getElementById('install')?.scrollIntoView();
  },[location.hash,installConfig]);
  return (
    <div className="min-h-screen bg-brand-900 font-inter text-brand-50">
      <Header installHere={installAvailable}/>

      <main>
        {/* Hero — directional lines follow mouse */}
        <section className="relative flex min-h-screen flex-col items-center justify-center px-5 pt-28 pb-16 text-center">
          <ForceField />
          <h1
            className="relative z-10 max-w-5xl leading-[0.95] tracking-normal text-brand-50"
            style={{ fontSize: 'clamp(50px, 7.5vw, 104px)', fontWeight: 500, textWrap: 'balance' }}
          >
            Your product and design lead for building with AI.
          </h1>
          <p className="relative z-10 mx-auto mt-7 max-w-2xl text-lg leading-relaxed text-brand-200">Pabs helps you decide what’s next, guides your AI tools and reviews their work with product and design expertise.</p>
          <div className="relative z-10 mt-6 sm:mt-10"><GettingStartedActions installHere={installAvailable} installFirst/>
            {installAvailable && <SupportedToolIcons config={installConfig}/>}
            <p className="mt-6 text-sm leading-relaxed text-brand-300">Just have an idea? <Link to={appEntryDestination()} className="underline underline-offset-4 hover:text-brand-50">Get started</Link></p>
          </div>
        </section>

        <DotDivider />

        {/* Section 1 — dots repel from mouse */}
        <section className="relative overflow-hidden">
          <ParticleField />
          <div className="mx-auto flex min-h-[70vh] max-w-3xl flex-col justify-center px-5 py-20">
            <WordReveal
              text="Your AI builders are flying blind."
              className="relative leading-[0.95] tracking-normal text-brand-50"
              style={{ fontSize: 'clamp(42px, 6vw, 84px)', fontWeight: 500 }}
            />
            <StaggeredList>
              <p className="text-xl font-medium text-brand-400">They execute fast.</p>
              <p className="text-xl font-medium text-brand-400">They just don’t know why you’re building.</p>
              <p className="text-xl font-medium text-brand-400">Credits burned building the wrong thing.</p>
              <p className="text-xl font-medium text-brand-400">Generic designs. Inconsistent quality.</p>
              <p className="text-xl font-medium text-brand-50">Pabs fixes that.</p>
            </StaggeredList>
            <a href="/research/pilot/story" className="mt-6 inline-flex min-h-[44px] w-fit items-center gap-2 text-sm font-medium text-brand-200 underline underline-offset-4 hover:text-brand-50">
              Read the pilot research <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          </div>
        </section>

        <DotDivider />

        {/* Section 2 — dots repel from mouse */}
        <section className="relative overflow-hidden">
          <ParticleField />
          <div className="mx-auto flex min-h-[70vh] max-w-3xl flex-col justify-center px-5 py-20">
            <WordReveal
              text="You didn’t start this to manage AI tools."
              className="relative leading-[0.95] tracking-normal text-brand-50"
              style={{ fontSize: 'clamp(36px, 4.8vw, 70px)', fontWeight: 500 }}
            />
            <StaggeredList>
              <p className="text-xl font-medium text-brand-400">You wanted to prototype or build something that matters.</p>
              <p className="text-xl font-medium text-brand-50">But the subscriptions keep adding up.</p>
              <p className="text-xl font-medium text-brand-50">You’re repeating context and cleaning up AI slop.</p>
              <p className="text-xl font-medium text-brand-50">Hiring product and design leads? Around US$350k/year.</p>
              <p className="text-xl font-medium text-brand-50">Paying in money. Paying in time.</p>
            </StaggeredList>
            <p className="mt-5 text-sm leading-relaxed text-brand-300">
              Illustrative combined US startup base salaries, using 2025–26 posting medians:{' '}
              <a href="https://www.recruitingfromscratch.com/roles/product-lead" target="_blank" rel="noreferrer" className="underline underline-offset-4 hover:text-brand-50">product lead</a>{' '}and{' '}
              <a href="https://www.recruitingfromscratch.com/roles/design-lead" target="_blank" rel="noreferrer" className="underline underline-offset-4 hover:text-brand-50">design lead</a>.
            </p>
          </div>
        </section>

        <DotDivider />

        {/* Section 3 — dots repel from mouse */}
        <section className="relative overflow-hidden">
          <ParticleField />
          <div className="mx-auto flex min-h-[70vh] max-w-3xl flex-col justify-center px-5 py-20">
            <WordReveal
              text="Pabs is the product function behind your AI workforce."
              className="relative leading-[0.95] tracking-normal text-brand-50"
              style={{ fontSize: 'clamp(42px, 6vw, 84px)', fontWeight: 500 }}
            />
            <StaggeredList>
              <p className="text-xl font-medium text-brand-50">Pabs does the product management work.</p>
              <p className="text-xl font-medium text-brand-300">Pabs helps you decide what deserves to be built.</p>
              <p className="text-xl font-medium text-brand-300">Pabs sets direction at the pace you build.</p>
              <p className="text-xl font-medium text-brand-300">Pabs tests whether it can become a business.</p>
              <p className="text-xl font-medium text-brand-300">Pabs turns strategy into instructions agents can use.</p>
              <p className="text-xl font-medium text-brand-300">Pabs sets the design standards before agents improvise.</p>
              <p className="text-xl font-medium text-brand-300">Pabs reviews concepts before they become direction.</p>
              <p className="text-xl font-medium text-brand-300">Pabs checks the build against what was agreed.</p>
              <p className="text-xl font-medium text-brand-300">Pabs catches accessibility and design-system drift.</p>
              <p className="text-xl font-medium text-brand-300">Pabs sends weak work back for correction.</p>
              <p className="text-xl font-medium text-brand-300">Pabs captures what customers, markets and code teach you.</p>
              <p className="text-xl font-medium text-brand-300">Pabs proposes how the direction should change.</p>
              <p className="text-xl font-medium text-brand-300">Pabs keeps consequential decisions with people.</p>
              <p className="text-xl font-medium text-brand-200">One authority layer. Every agent. Every build.</p>
              <p className="text-xl font-medium text-brand-50">Your product judgment, always on.</p>
            </StaggeredList>
            <a href="/research/pilot/the-data" className="mt-6 inline-flex min-h-[44px] w-fit items-center gap-2 text-sm font-medium text-brand-200 underline underline-offset-4 hover:text-brand-50">
              Explore the pilot data <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
            <div><CTA installHref={installHref} secondary={{ to: '/platform', label: 'See the platform' }} /></div>
          </div>
        </section>

        <DotDivider />

        {clientInstallAvailable(installConfig) && <section id="install" className="scroll-mt-24 px-5 py-20"><div className="mx-auto max-w-5xl"><ClientInstallInstructions config={installConfig} appearance="dark" layout="feature"/></div></section>}
        <TestimonialsSection />

        <DotDivider />

        {/* Closing */}
        <section className="relative flex min-h-[50vh] flex-col items-center justify-center px-5 py-20 text-center">
          <h2
            className="relative max-w-3xl leading-[0.95] tracking-normal text-brand-50"
            style={{ fontSize: 'clamp(42px, 6vw, 84px)', fontWeight: 500 }}
          >
            Build the thing that can become a business.
          </h2>
          <CTA installHref={installHref}/>
        </section>
      </main>

      <Footer />
    </div>
  );
}
